import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { FileRejection } from 'react-dropzone';

import { Store } from 'components/common/types/Store.types';
import { PermissionsEnum } from 'consts/permissions';
import { NotificationMessages } from 'consts/notifications';
import Auth from 'modules/Auth';
import { fetchSetupFileDetails, fetchSetupFileDetailsV2, uploadSetupFile, uploadSetupFileV2 } from 'modules/api/setup';
import { formatDateTime } from 'modules/I18N';
import { addToastNotification } from 'store/notification/reducer';
import { cancelFunctions } from 'utils/cancelFunctions';
import { Color } from 'lib/Chip';
import { FileSuccess } from 'components/common/types/File.types';
import {
  FileUploadStatusCode,
  SetupDetailedFrameInformation,
  SetupDetails,
  SetupFrameInformation,
} from 'components/pages/InventoryManagement/Setup/Setup.types';
import { isPollingState } from 'components/pages/InventoryManagement/Setup/utils';
import DownloadRenderer from './DownloadRenderer';
import InventoryStatusRenderer from './InventoryStatusRenderer';
import ErrorDetailRenderer from './ErrorDetailRenderer';
import ProgressRenderer from './ProgressRenderer';
import FileNameRenderer from './FileNameRenderer';
import UploadedByRenderer from './UploadedByRenderer';

interface ProcessingStatus {
  code: FileUploadStatusCode;
  name: string;
  color: Color;
  initials?: string;
}

interface UseSetupContentProps {
  isAssetTemplateV2Enabled: boolean | null;
  resetTableApi: () => void;
}

interface UseSetupContentResult {
  fileList: FileSuccess[];
  fileName: string;
  handleFileList: (list: FileSuccess[]) => void;
  handleFileName: (name: string) => void;
  onFileChange: (acceptedFiles: FileSuccess[], rejectedFiles: FileRejection[]) => void;
  onUploadFiles: () => Promise<void>;
  isOpen: boolean;
  handleCloseModal: () => void;
  setupDetails: SetupDetails;
  columnDefs: ColDef<SetupFrameInformation>[];
}

const getProcessingStatuses = (isAssetTemplateV2Enabled: boolean = false): ProcessingStatus[] => [
  {
    code: FileUploadStatusCode.SUCCESS,
    name: isAssetTemplateV2Enabled ? 'Data Fully Loaded' : 'Success',
    initials: !isAssetTemplateV2Enabled ? 'S' : undefined,
    color: Color.GREEN,
  },
  {
    code: FileUploadStatusCode.PARTIAL_FAILURE,
    name: isAssetTemplateV2Enabled ? 'Data Partially Loaded' : 'Failed',
    color: Color.YELLOW,
  },
  {
    code: FileUploadStatusCode.FAILED,
    name: isAssetTemplateV2Enabled ? 'All Data Failed to Load' : 'Failed',
    initials: !isAssetTemplateV2Enabled ? 'F' : undefined,
    color: Color.RED,
  },
  {
    code: FileUploadStatusCode.ERROR,
    name: isAssetTemplateV2Enabled ? 'All Data Failed to Load' : 'Error',
    initials: !isAssetTemplateV2Enabled ? 'E' : undefined,
    color: Color.RED,
  },
  {
    code: FileUploadStatusCode.WAITING,
    name: 'Waiting',
    initials: !isAssetTemplateV2Enabled ? 'W' : undefined,
    color: Color.YELLOW,
  },
  {
    code: FileUploadStatusCode.PROCESSING,
    name: 'Processing',
    initials: !isAssetTemplateV2Enabled ? 'P' : undefined,
    color: Color.YELLOW,
  },
];

export const useSetupContent = ({
  resetTableApi,
  isAssetTemplateV2Enabled,
}: UseSetupContentProps): UseSetupContentResult => {
  const [fileList, setFileList] = useState<FileSuccess[]>([]);
  const [fileName, setFileName] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const [setupDetails, setSetupDetails] = useState<SetupDetails>({
    hasErrors: true,
    fileName: '',
    statusCode: FileUploadStatusCode.ERROR,
    setupDetailResponse: [],
  });

  const dispatch = useDispatch();

  const { localeCode } = useSelector((state: Store) => state.publisher.configuration);

  const handleFileList = (list: FileSuccess[]): void => setFileList(list);
  const handleFileName = (name: string): void => setFileName(name);

  const onFileChange = (acceptedFiles: FileSuccess[], rejectedFiles: FileRejection[]): void => {
    if (acceptedFiles.length > 0) {
      setFileList(acceptedFiles);
      setFileName(acceptedFiles[0].name);
    }

    if (rejectedFiles.length > 0) {
      dispatch(
        addToastNotification({ description: NotificationMessages.INVALID_UPLOAD, type: 'Warning', timeout: 10000 }),
      );
    }
  };

  const onUploadFiles = async (): Promise<void> => {
    if (!fileList.length) {
      dispatch(addToastNotification({ description: 'Please select file to upload', type: 'Warning', timeout: 2000 }));
      return;
    }

    if (isAssetTemplateV2Enabled === null) {
      dispatch(
        addToastNotification({
          description: 'Setup configuration is still loading. Please try again.',
          type: 'Warning',
          timeout: 2000,
        }),
      );
      return;
    }

    const formData = new FormData();
    for (let i = 0; i < fileList.length; i++) {
      formData.append('files', fileList[i]);
    }

    try {
      const uploadSetupFileRequest = isAssetTemplateV2Enabled ? uploadSetupFileV2 : uploadSetupFile;
      const result = await uploadSetupFileRequest(formData, cancelFunctions);
      const { setupResponses } = result;

      if (setupResponses[0].uploadStatusCode === FileUploadStatusCode.FAILED) {
        dispatch(
          addToastNotification({
            description: `${setupResponses[0].fileName} has been ${setupResponses[0].uploadStatusCode} ${setupResponses[0].errorDetail}`,
            type: 'Warning',
            timeout: 10000,
          }),
        );
      } else {
        dispatch(
          addToastNotification({
            description: `${setupResponses[0].fileName} successfully uploaded with status ${setupResponses[0].uploadStatusCode}`,
            timeout: 10000,
            type: 'Success',
          }),
        );
      }

      handleFileList([]);
      handleFileName('');
      resetTableApi();
      // eslint-disable-next-line no-empty
    } catch {}
  };

  const onStatusDetails = async (id: string, name: string, statusCode: FileUploadStatusCode): Promise<void> => {
    if (!isPollingState(statusCode)) {
      try {
        const fetchSetupFileDetailsRequest = isAssetTemplateV2Enabled ? fetchSetupFileDetailsV2 : fetchSetupFileDetails;
        const response = await fetchSetupFileDetailsRequest(id, cancelFunctions);
        const checkHasError = (element: SetupDetailedFrameInformation): boolean => element.errorRecords > 0;
        setSetupDetails({
          hasErrors: response.setupDetailResponse.some(checkHasError),
          fileName: name,
          statusCode,
          setupDetailResponse: response.setupDetailResponse,
        });
        setIsOpen(true);
      } catch {} // eslint-disable-line no-empty
    }
  };

  const handleCloseModal = (): void => {
    setIsOpen(false);
  };

  const columnDefs = useMemo((): ColDef<SetupFrameInformation>[] => {
    if (isAssetTemplateV2Enabled === null) {
      return [];
    }

    return [
      ...(isAssetTemplateV2Enabled
        ? []
        : [
            {
              headerName: '',
              minWidth: 20,
              maxWidth: 20,
              headerTooltip: '',
              resizable: false,
            },
            {
              headerName: 'Status',
              field: 'uploadStatusCode',
              minWidth: 150,
              maxWidth: 150,
              resizable: false,
              cellRenderer: InventoryStatusRenderer,
              cellRendererParams: {
                statuses: getProcessingStatuses(),
                onClick: (id: string, name: string, statusCode: FileUploadStatusCode) =>
                  onStatusDetails(id, name, statusCode),
              },
              colId: 'status',
            },
          ]),
      {
        headerName: 'File name',
        field: 'fileName',
        minWidth: 340,
        cellRenderer: FileNameRenderer,
        cellRendererParams: {
          isAssetTemplateV2Enabled,
        },
      },
      {
        headerName: 'Date',
        field: 'uploadDate',
        minWidth: !isAssetTemplateV2Enabled ? 270 : undefined,
        cellRenderer: (params: ICellRendererParams) => {
          if (params.data) {
            return formatDateTime({ isoDateString: params.data.uploadDate, localeCode, dateStyle: 'short' });
          }
          return '';
        },
      },
      {
        headerName: 'Uploaded by',
        field: 'uploadUserName',
        minWidth: !isAssetTemplateV2Enabled ? 120 : 210,
        resizable: false,
        cellRenderer: UploadedByRenderer,
      },
      ...(isAssetTemplateV2Enabled
        ? [
            {
              headerName: 'Progress',
              field: 'progressPercentage',
              resizable: false,
              maxWidth: 210,
              cellRenderer: ProgressRenderer,
            },
            {
              headerName: 'Status',
              field: 'processingState',
              maxWidth: 210,
              resizable: false,
              cellRenderer: InventoryStatusRenderer,
              cellRendererParams: {
                isAssetTemplateV2Enabled,
                statuses: getProcessingStatuses(isAssetTemplateV2Enabled),
                onClick: (id: string, name: string, statusCode: FileUploadStatusCode) =>
                  onStatusDetails(id, name, statusCode),
              },
              colId: 'status',
            },
            {
              headerName: 'Error message',
              field: 'errorDetail',
              resizable: false,
              cellRenderer: ErrorDetailRenderer,
            },
          ]
        : [
            {
              headerName: '',
              cellRenderer: DownloadRenderer,
              minWidth: 50,
              resizable: false,
              hide: !Auth.hasPermission(PermissionsEnum.SETUP_DOWNLOAD),
            },
          ]),
    ];
  }, [isAssetTemplateV2Enabled, localeCode]);

  return {
    fileList,
    fileName,
    handleFileList,
    handleFileName,
    onFileChange,
    onUploadFiles,
    setupDetails,
    columnDefs,
    isOpen,
    handleCloseModal,
  };
};
